/**
 * NgwEditorMailInsert — `IMailEditorInsert` 의 NgwEditor 본 구현 (PR-N5).
 *
 * 5 메서드 모두 `extensions/{timeSlot,availability,sharing,officeNote,messageHistory}.ts` 의
 * NgwExtension 명령으로 위임 — `lifecycle.getHandle().executeCommand(...)` 경유.
 *
 * - `timeSlotSpace(height)` → `executeCommand('insertTimeSlotSpace', { height })`
 * - `addSendAvailability(payload)` → `executeCommand('addSendAvailability', payload)`
 *   (payload.id 가 제공되면 doc 안 동일 id 노드 갱신, 미매칭 시 신규 삽입 fallback).
 *   신규 삽입은 서명/인용문 위로 selection 을 보정한다 — `ensureSelectionAboveTrailingRegions`
 * - `addSharingAttachments(payload)` → `executeCommand('addSharingAttachments', payload)`
 * - `insertOfficeNote(payload, isHTML)` → `executeCommand('insertOfficeNote', payload, isHTML)`
 *   (isHTML 분기는 명령 안에서 처리 — PMDOMParser vs text node)
 * - `showMessageHistory()` → `executeCommand('triggerShowMessageHistory')`
 *   (handler.messageHistoryAppended + quotedButton.triggerInsert 결합)
 *
 * init 전 (`lifecycle.getHandle()` 이 null) 호출 시 silent — TinyMCEInsert 의 `editorWindow.*`
 * 위임과 동일한 보호 시맨틱.
 *
 * PR-N4 의 stub (NoOp + 1회 warn) 자리를 본 구현으로 교체 — `console.warn` 발화 0건.
 */

import type { IMailEditorInsert } from '../IMailEditorInsert'
import type { OfficeNoteData, SendAvailabilityPayload, SharingAttachmentsPayload } from '../types'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

export class NgwEditorMailInsert implements IMailEditorInsert {
  private readonly adapter: NgwEditorAdapter

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  timeSlotSpace(height: number): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    handle.executeCommand('insertTimeSlotSpace', { height })
  }

  addSendAvailability(payload: SendAvailabilityPayload): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    // 신규 삽입은 항상 **서명/인용문 위**(사용자 본문)에 놓는다. 갱신(payload.id)은 노드 위치를
    // 직접 찾아 setNodeMarkup 하므로 selection 과 무관 — 사용자 cursor 를 건드리지 않는다.
    if (payload.id == null || payload.id.length === 0) {
      this.ensureSelectionAboveTrailingRegions()
    }
    handle.executeCommand('addSendAvailability', payload)
  }

  /**
   * `addSendAvailability` 명령은 현 selection 위치에 삽입한다(`extensions/availability.ts` 의
   * `state.tr.replaceWith(from, to, …)`). 그런데 compose 진입 시 기본 서명이 이미 삽입돼 있고
   * 사용자가 본문을 클릭하지 않은 상태면 selection 이 서명 뒤(=doc 끝)에 있을 수 있어 시간표가
   * **서명 아래**에 꽂힌다 (2026-08-06 사용자 보고).
   *
   * Web(TinyMCE)은 `editor.insertContent` 로 같은 "현 cursor" 시맨틱이지만, EmailForm 이 본문
   * 편집 흐름 안에서 호출하므로 cursor 가 이미 nbody 에 있어 문제가 드러나지 않았다. 여기서는
   * 그 전제를 명시적으로 보장한다.
   *
   * **사용자가 본문 안에 의도적으로 둔 cursor 는 보존한다** — selection 이 이미 서명/인용문
   * 경계보다 앞이면 아무것도 하지 않는다. 경계 뒤(또는 경계 위)일 때만 본문 끝으로 옮긴다.
   * 옮기는 위치 산출은 `cursor.setIntoNBodyCore(false)` 가 단독 소유한다(nbody region → 없으면
   * signature/quoted 직전 → 없으면 doc 끝).
   */
  private ensureSelectionAboveTrailingRegions(): void {
    const core = this.adapter.lifecycle.getCore()
    const handle = this.adapter.lifecycle.getHandle()
    if (core == null || handle == null) return

    const trailingRegions: ReadonlyArray<string> = ['signature', 'quoted_header', 'quoted_body']
    let earliestPos: number | null = null
    for (const regionName of trailingRegions) {
      for (const entry of handle.findNodes(regionName)) {
        if (earliestPos === null || entry.pos < earliestPos) {
          earliestPos = entry.pos
        }
      }
    }
    // 서명·인용문이 아예 없으면 어디에 넣어도 "위" 이므로 cursor 를 존중한다.
    if (earliestPos === null) return

    if (core.view.state.selection.from < earliestPos) return
    this.adapter.cursor.setIntoNBodyCore(false)
  }

  addSharingAttachments(payload: SharingAttachmentsPayload): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    handle.executeCommand('addSharingAttachments', payload)
  }

  insertOfficeNote(payload: OfficeNoteData, isHTML: boolean): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    handle.executeCommand('insertOfficeNote', payload, isHTML)
  }

  showMessageHistory(): void {
    const handle = this.adapter.lifecycle.getHandle()
    if (handle == null) return
    handle.executeCommand('triggerShowMessageHistory')
  }
}
